import { useState, useRef } from "react";
import axiosConfig from "../../utils/axiosConfig";
import { baseUrl } from "../../config";
import {
  BusinessUnitOption,
  VerticalOption,
  CustomerGroupOption,
  CustomerOption,
  LoadingStates,
  BusinessUnitsResponse,
  VerticalsResponse,
  CustomerGroupsResponse,
  CustomersResponse,
  PrefillValues,
  ServiceLineOption,
  ItemWithAccountId,
  ItemWithCustomerId,
} from "./type";
import type {
  ServiceLineFilterTreeResponse,
  ServiceLineTreeNode,
} from "./components/UnitHierarchyFilter/serviceLineTreeTypes";
import {
  buildServiceLineOptionsFromPayload,
  buildServiceLinePrefillOption,
  normalizeServiceLineRoots,
} from "./components/UnitHierarchyFilter/serviceLineTreeUtils";

const transformCustomerGroupData = (
  data: ItemWithAccountId[],
): CustomerGroupOption[] => {
  const allItems = data.filter((item) => item.space_account_id === null);
  const regularItems = data.filter((item) => item.space_account_id !== null);

  const transformedRegularItems: CustomerGroupOption[] = regularItems.map(
    (item) => ({
      label: item.name,
      value: item.space_account_id?.toString() ?? "",
    }),
  );

  const finalOptions: CustomerGroupOption[] = [];
  if (allItems.length > 0) {
    finalOptions.push({
      label: allItems[0].name || "All",
      value: "___EMPTY___",
    });
  }

  finalOptions.push(...transformedRegularItems);
  return finalOptions;
};

const transformCustomerData = (
  data: ItemWithCustomerId[],
): CustomerOption[] => {
  const allItems = data.filter((item) => item.space_customer_id === null);
  const regularItems = data.filter((item) => item.space_customer_id !== null);

  const transformedRegularItems: CustomerOption[] = regularItems.map(
    (item) => ({
      label: item.name,
      value: item.space_customer_id?.toString() ?? "",
    }),
  );

  const finalOptions: CustomerOption[] = [];
  if (allItems.length > 0) {
    finalOptions.push({
      label: allItems[0].name || "All",
      value: "___EMPTY___",
    });
  }

  finalOptions.push(...transformedRegularItems);
  return finalOptions;
};

const createCustomerGroupPrefillOption = (prefillData: {
  name: string;
  space_account_id: string | null;
}): CustomerGroupOption => ({
  label: prefillData.name,
  value:
    prefillData.space_account_id === null
      ? "___EMPTY___"
      : (prefillData.space_account_id?.toString() ?? ""),
});

const createCustomerPrefillOption = (prefillData: {
  name: string;
  space_customer_id: string | null;
}): CustomerOption => ({
  label: prefillData.name,
  value:
    prefillData.space_customer_id === null
      ? "___EMPTY___"
      : (prefillData.space_customer_id?.toString() ?? ""),
});

export const useOperationsDashboardData = () => {
  const [businessUnits, setBusinessUnits] = useState<BusinessUnitOption[]>([]);
  const [businessUnitFilterTree, setBusinessUnitFilterTree] = useState<
    ServiceLineTreeNode[] | null
  >(null);
  const [verticalOptions, setVerticalOptions] = useState<VerticalOption[]>([]);
  const [serviceLineOptions, setServiceLineOptions] = useState<
    ServiceLineOption[]
  >([]);
  const [serviceLineFilterTree, setServiceLineFilterTree] = useState<
    ServiceLineTreeNode[] | null
  >(null);
  const [customerGroupOptions, setCustomerGroupOptions] = useState<
    CustomerGroupOption[]
  >([]);
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [prefillValues, setPrefillValues] = useState<PrefillValues>({});
  const [loading, setLoading] = useState<LoadingStates>({
    businessUnits: false,
    verticals: false,
    customerGroupOptions: false,
    customerOptions: false,
    serviceLineOptions: false,
  });

  const customerGroupAbortController = useRef<AbortController | null>(null);
  const customerAbortController = useRef<AbortController | null>(null);

  const fetchBusinessUnits = async () => {
    setLoading((prev) => ({ ...prev, businessUnits: true }));

    try {
      const response = await axiosConfig({
        method: "get",
        url: `${baseUrl}/operational-dashboard/organizational-units?unit_type=${encodeURIComponent(
          "Business Unit",
        )}`,
      });

      if (response.status === 200) {
        const responseData: BusinessUnitsResponse = response.data;

        if (Array.isArray(responseData.data) && responseData.data.length > 0) {
          const normalized = normalizeServiceLineRoots(responseData.data);
          setBusinessUnitFilterTree(normalized);
          setBusinessUnits(
            buildServiceLineOptionsFromPayload(
              normalized,
            ) as BusinessUnitOption[],
          );
        } else {
          setBusinessUnitFilterTree(null);
          setBusinessUnits([]);
        }

        if (responseData.prefill_data) {
          const prefillOption = buildServiceLinePrefillOption(
            responseData.prefill_data,
          ) as BusinessUnitOption;

          setPrefillValues((prev) => ({
            ...prev,
            businessUnit: prefillOption,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching business units:", error);
      setBusinessUnits([]);
      setBusinessUnitFilterTree(null);
    } finally {
      setLoading((prev) => ({ ...prev, businessUnits: false }));
    }
  };

  const fetchServiceLineOptions = async () => {
    setLoading((prev) => ({ ...prev, serviceLineOptions: true }));

    try {
      const response = await axiosConfig({
        method: "get",
        url: `${baseUrl}/operational-dashboard/organizational-units?unit_type=${encodeURIComponent(
          "Service Line",
        )}`,
      });

      if (response.status === 200) {
        const responseData: ServiceLineFilterTreeResponse = response.data;

        if (Array.isArray(responseData.data) && responseData.data.length > 0) {
          const normalized = normalizeServiceLineRoots(
            responseData.data as ServiceLineTreeNode[],
          );
          setServiceLineFilterTree(normalized);
          setServiceLineOptions(buildServiceLineOptionsFromPayload(normalized));
        } else {
          setServiceLineFilterTree(null);
          setServiceLineOptions([]);
        }

        if (responseData.prefill_data) {
          const prefillOption = buildServiceLinePrefillOption(
            responseData.prefill_data,
          );

          setPrefillValues((prev) => ({
            ...prev,
            serviceLine: prefillOption,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching service line options:", error);
      setServiceLineOptions([]);
      setServiceLineFilterTree(null);
    } finally {
      setLoading((prev) => ({ ...prev, serviceLineOptions: false }));
    }
  };

  const fetchVerticalOptions = async (organizational_unit_id?: string) => {
    setLoading((prev) => ({ ...prev, verticals: true }));
    try {
      const response = await axiosConfig({
        method: "get",
        url: `${baseUrl}/operational-dashboard/verticals`,
        params: {
          organizational_unit_id,
        },
      });

      if (response.status === 200) {
        const responseData: VerticalsResponse = response.data;

        if (responseData.data) {
          const transformedData: VerticalOption[] = responseData.data.map(
            (item) => ({
              label: item.name,
              value: item.space_vertical_id?.toString() ?? "",
            }),
          );
          setVerticalOptions(transformedData);
        }

        if (responseData.prefill_data) {
          const prefillOption: VerticalOption = {
            label: responseData.prefill_data.name,
            value:
              responseData.prefill_data.space_vertical_id?.toString() ?? "",
          };

          setPrefillValues((prev) => ({
            ...prev,
            vertical: prefillOption,
          }));
        }
      } else {
        setVerticalOptions([]);
      }
    } catch (error) {
      console.error("Error fetching vertical options:", error);
      setVerticalOptions([]);
    } finally {
      setLoading((prev) => ({ ...prev, verticals: false }));
    }
  };

  const fetchCustomerGroupOptions = async ({
    business_unit_id,
    vertical,
    service_line_id,
    start_year,
    end_year,
    start_month,
    end_month,
  }: {
    business_unit_id?: string;
    vertical?: string;
    bu_child?: string;
    service_line_id?: string;
    start_year?: number;
    end_year?: number;
    start_month?: number;
    end_month?: number;
  }) => {
    if (customerGroupAbortController.current) {
      customerGroupAbortController.current.abort();
    }

    const currentController = new AbortController();
    customerGroupAbortController.current = currentController;

    setLoading((prev) => ({ ...prev, customerGroupOptions: true }));
    try {
      const response = await axiosConfig({
        method: "get",
        url: `${baseUrl}/operational-dashboard/customer-groups`,
        params: {
          business_unit_id,
          vertical,
          service_line_id,
          start_year,
          end_year,
          start_month,
          end_month,
        },
        signal: currentController.signal,
      });

      if (response.status === 200) {
        const responseData: CustomerGroupsResponse = response.data;

        if (responseData.data) {
          const finalOptions = transformCustomerGroupData(
            responseData.data as ItemWithAccountId[],
          );
          setCustomerGroupOptions(finalOptions);
        } else {
          setCustomerGroupOptions([]);
        }

        if (responseData.prefill_data) {
          const prefillOption = createCustomerGroupPrefillOption({
            name: responseData.prefill_data.name,
            space_account_id: responseData.prefill_data.space_account_id as
              | string
              | null,
          });

          setPrefillValues((prev) => ({
            ...prev,
            customerGroup: prefillOption,
          }));
        }
      }
    } catch (error: any) {
      if (error.name !== "CanceledError" && error.code !== "ERR_CANCELED") {
        console.error("Error fetching customer group options:", error);
        setCustomerGroupOptions([]);
      }
    } finally {
      if (customerGroupAbortController.current === currentController) {
        setLoading((prev) => ({ ...prev, customerGroupOptions: false }));
      }
    }
  };

  const fetchCustomerOptions = async ({
    business_unit_id,
    vertical,
    bu_child,
    service_line_id,
    customer_group,
    start_year,
    end_year,
    start_month,
    end_month,
  }: {
    business_unit_id?: string;
    vertical?: string;
    bu_child?: string;
    service_line_id?: string;
    customer_group?: string;
    start_year?: number;
    end_year?: number;
    start_month?: number;
    end_month?: number;
  }) => {
    if (customerAbortController.current) {
      customerAbortController.current.abort();
    }

    const currentController = new AbortController();
    customerAbortController.current = currentController;

    setLoading((prev) => ({ ...prev, customerOptions: true }));
    try {
      const response = await axiosConfig({
        method: "get",
        url: `${baseUrl}/operational-dashboard/customers`,
        params: {
          business_unit_id,
          vertical,
          bu_child,
          service_line_id,
          customer_group,
          start_year,
          end_year,
          start_month,
          end_month,
        },
        signal: currentController.signal,
      });

      if (response.status === 200) {
        const responseData: CustomersResponse = response.data;

        if (responseData.data) {
          const finalOptions = transformCustomerData(
            responseData.data as ItemWithCustomerId[],
          );
          setCustomerOptions(finalOptions);
        } else {
          setCustomerOptions([]);
        }

        if (responseData.prefill_data) {
          const prefillOption = createCustomerPrefillOption({
            name: responseData.prefill_data.name,
            space_customer_id: responseData.prefill_data.space_customer_id as
              | string
              | null,
          });

          setPrefillValues((prev) => ({
            ...prev,
            customer: prefillOption,
          }));
        }
      }
    } catch (error: any) {
      if (error.name !== "CanceledError" && error.code !== "ERR_CANCELED") {
        console.error("Error fetching customer options:", error);
        setCustomerOptions([]);
      }
    } finally {
      if (customerAbortController.current === currentController) {
        setLoading((prev) => ({ ...prev, customerOptions: false }));
      }
    }
  };

  const clearVerticalOptions = () => {
    setVerticalOptions([]);
  };
  const clearCustomerGroupOptions = () => {
    setCustomerGroupOptions([]);
  };
  const clearCustomerOptions = () => {
    setCustomerOptions([]);
  };

  return {
    businessUnits,
    businessUnitFilterTree,
    loading,
    serviceLineOptions,
    serviceLineFilterTree,
    fetchServiceLineOptions,
    fetchBusinessUnits,
    verticalOptions,
    fetchVerticalOptions,
    clearVerticalOptions,
    clearCustomerGroupOptions,
    clearCustomerOptions,
    customerGroupOptions,
    fetchCustomerGroupOptions,
    customerOptions,
    fetchCustomerOptions,
    prefillValues,
  };
};
